import * as R from 'ramda'
import {
  CategoryType,
  EventEditMessagePayload,
  GetMessagesCountByType,
  Message,
  reqGetMessagesCountByType,
  IMessage,
} from '@weblite/ts-action/message'
import { getPanelTop } from 'weblite-router'
import { Mutex } from 'async-mutex'
import {
  NextAndPrevByType,
  SharedMediaCategoryType,
  SharedMediaData,
} from '$logic/shared-media/types'
import { messagesStorage } from '$logic/message/storage'
import { openedRoomSharedMediaStorage } from '$logic/shared-media/opened-room-shared-media.storage'
import { sharedMediaStorage } from '$logic/shared-media/shared-media.storage'
import { getRoom, getRoomFromRedux } from '$logic/room'
import {
  FETCH_MESSAGES_WINDOW_SIZE,
  getMessages,
  getMessagesByCategory,
  getMessagesByCategoryAndRange,
} from '$logic/message'
import { sharedMediaApi } from '$comp/_app/components/leftPanel/components/shared-media'
import { request } from '$setup/ws'
import { isPublicHall } from '$logic/hall'
import { getRoomLastMessage } from '$logic/room-last-message'

export const filterByCategory = (
  message: IMessage,
  type: SharedMediaCategoryType,
) => {
  if (
    type === 'MEDIA' &&
    (message.type === 'VIDEO' || message.type === 'IMAGE')
  )
    return true
  if (type === 'LINK' && message.links && message.links.length) return true
  return type === message.type
}

const mutex = new Mutex()

export async function loadMoreSharedMediaContents(
  roomId: string,
  category: SharedMediaCategoryType,
) {
  const room = await getRoom(roomId)
  const roomIid = room?.iid

  async function initiateSharedMediaData() {
    await sharedMediaStorage.clearByCategory(roomId, category)

    const { messages, fetchedAll } = await getMessagesByCategory(
      roomId,
      category,
      roomIid + 1,
    )

    await sharedMediaStorage.replace({
      roomId,
      category,
      iids: R.pluck('iid', messages),
      hasSeenFirst: fetchedAll,
      lastFetchIid: room.iid,
      totalCount: null,
    })
  }

  async function loadMediaFromMessageService(sharedMediaData: SharedMediaData) {
    const messages = await getMessages(roomId, {
      range: [sharedMediaData.lastFetchIid + 1, roomIid],
    })
    const filteredMessages = R.reverse(
      R.sortBy(
        R.prop('iid'),
        messages.filter(message =>
          filterByCategory(message as IMessage, category),
        ),
      ),
    )
    const item = await sharedMediaStorage.get(roomId, category)
    await sharedMediaStorage.append(
      roomId,
      category,
      [...R.pluck('iid', filteredMessages), ...item.iids],
      room.iid,
    )
  }

  async function loadMoreMedia(sharedMediaData: SharedMediaData) {
    const upperIid = sharedMediaData.iids
      ? R.last(sharedMediaData.iids)
      : roomIid + 1
    const { messages, fetchedAll } = await getMessagesByCategory(
      roomId,
      category,
      upperIid,
    )

    if (!messages.length)
      await sharedMediaStorage.update([roomId, category], {
        hasSeenFirst: fetchedAll,
      })
    else
      await sharedMediaStorage.prepend(
        roomId,
        category,
        [...sharedMediaData.iids, ...R.pluck('iid', messages)],
        fetchedAll,
      )
  }

  await mutex.runExclusive(async () => {
    const sharedMediaData = await sharedMediaStorage.get(roomId, category)

    if (sharedMediaData?.lastFetchIid) {
      if (roomIid > sharedMediaData.lastFetchIid + FETCH_MESSAGES_WINDOW_SIZE) {
        await initiateSharedMediaData()
      } else if (
        roomIid > sharedMediaData.lastFetchIid &&
        roomIid <= sharedMediaData.lastFetchIid + FETCH_MESSAGES_WINDOW_SIZE
      ) {
        await loadMediaFromMessageService(sharedMediaData)
      } else if (roomIid === sharedMediaData.lastFetchIid) {
        await loadMoreMedia(sharedMediaData)
      }
    } else await initiateSharedMediaData()
    await calcSharedMediaTotalCount(roomId, category)

    const { iids } = await sharedMediaStorage.get(roomId, category)
    if (iids) await copyMessagesFromDBToRedux(roomId, iids)
  })
}

export async function initiateSharedMediaContents(
  roomId: string,
  category: SharedMediaCategoryType,
) {
  const sharedMediaData = await sharedMediaStorage.get(roomId, category)

  const room = await getRoom(roomId)
  const roomIid = room?.iid

  if (roomIid && roomIid === sharedMediaData?.lastFetchIid)
    await copyMessagesFromDBToRedux(roomId, sharedMediaData.iids)
  else await loadMoreSharedMediaContents(roomId, category)
}

// TODO: Should invalidate cache if message is erased.
export const getSharedMediaContentCounter = R.memoizeWith(
  roomId => `${roomId + getRoomFromRedux(roomId)?.iid}`,
  getMessageCountByType,
)

async function getMessageCountByType(roomId: string) {
  const res = await request<GetMessagesCountByType>(
    reqGetMessagesCountByType({
      roomId,
      publicHall: await isPublicHall(roomId),
    }),
  )
  return res.payload
}

export async function clearRoomSharedMediaDB(roomId: string) {
  return sharedMediaStorage.clear(roomId)
}

export async function copyMessagesFromDBToRedux(
  roomId: string,
  iids: number[],
) {
  const medias = (await messagesStorage.findByIids(roomId, iids)).filter(
    media => !media.patches?.deleted,
  )
  openedRoomSharedMediaStorage.replace(medias)
}

export async function removeSharedMediaMessagesByIid(
  roomId: string,
  iids: number[],
) {
  const currentCategory = sharedMediaApi.getCategory()
  const sharedMedias = await sharedMediaStorage.get(roomId, currentCategory)
  if (!sharedMedias) return
  const newIids = R.without(iids, sharedMedias.iids)
  await sharedMediaStorage.update([roomId, currentCategory], {
    iids: newIids,
    totalCount: sharedMedias.totalCount - iids.length,
  })

  const openedRoomSharedMedias = openedRoomSharedMediaStorage.getAll()
  if (openedRoomSharedMedias && openedRoomSharedMedias.length)
    if (openedRoomSharedMedias[0].roomId === roomId)
      openedRoomSharedMediaStorage.deleteMessages(iids)
}

export function handleEditMessages(patches: EventEditMessagePayload[]) {
  const currentCategory = sharedMediaApi.getCategory()
  const openedSharedMediaRoomId = getOpenedSharedMediaRoomId()

  if (openedSharedMediaRoomId && currentCategory)
    patches.forEach(({ roomId, iid, ...updatedFields }) => {
      if (openedSharedMediaRoomId !== roomId) return

      const message = openedRoomSharedMediaStorage.get(iid)
      if (message && filterByCategory(message as IMessage, currentCategory))
        openedRoomSharedMediaStorage.editMessage(iid, updatedFields)
    })
}

export function handleNewMessage(messages: Message[]) {
  const roomId = getOpenedSharedMediaRoomId()
  if (!roomId) return
  const currentCategory = sharedMediaApi.getCategory()
  if (!currentCategory) return
  const filteredMessages = messages.filter(
    message =>
      message.roomId === roomId &&
      filterByCategory(message as IMessage, currentCategory),
  )
  if (!filteredMessages.length) return
  const currentSharedMedias = openedRoomSharedMediaStorage.getAll() // why this returns empty array when forward from a chat to that chat?
  const newSharedMedias = R.reverse(
    R.sortBy(
      R.prop('iid'),
      R.uniqBy(R.prop('iid'), [
        ...filteredMessages,
        ...currentSharedMedias,
      ]) as Message[],
    ),
  )
  openedRoomSharedMediaStorage.replace(newSharedMedias)
}

export function getOpenedSharedMediaRoomId() {
  const { location, state } = getPanelTop('roomProfile') || {}
  return location === 'roomProfile/SHARED_MEDIA' ? state.roomId : null
}

async function fillSharedMediaItems(
  roomId: string,
  category: CategoryType,
  upperIid: number,
  lowerIid?: number,
) {
  if (!roomId || !upperIid) return
  if (upperIid && lowerIid >= upperIid) return
  const {
    iids,
    hasSeenFirst,
    lastFetchIid,
    totalCount,
  } = (await sharedMediaStorage.get(roomId, category)) || {
    iids: [] as number[],
  }
  if (!lowerIid && hasSeenFirst) return
  // NOTE: we increase upper iid to have upper iid in result & don't have lower iid in result
  const { messages, fetchedAll } = await getMessagesByCategoryAndRange(
    roomId,
    category,
    upperIid + 1,
    lowerIid,
  )
  const { iid: lastMessageIid } = (await getRoomLastMessage(roomId)) || {}
  if (messages.length) {
    const filteredMessages = R.pluck(
      'iid',
      messages.filter(message =>
        filterByCategory(message as IMessage, category),
      ),
    )

    const newIids = R.sort(
      (a, b) => b - a,
      R.uniq(R.concat(filteredMessages, iids)),
    )

    await sharedMediaStorage.replace({
      roomId,
      iids: newIids,
      category,
      totalCount,
      hasSeenFirst: !lowerIid ? fetchedAll || hasSeenFirst : hasSeenFirst,
      lastFetchIid: lastMessageIid,
    })
  } else if (lastFetchIid !== lastMessageIid && lastFetchIid < lastMessageIid) {
    await sharedMediaStorage.update([roomId, category], {
      lastFetchIid: lastMessageIid,
    })
  }
}

const categoryType = ['IMAGE', 'VIDEO', 'VOICE', 'FILE', 'AUDIO']
export async function increaseSharedMediaTotalCount(rawMessages: Message[]) {
  const messages = rawMessages.filter((message: Message) =>
    categoryType.includes(message.type),
  )
  const grouped = R.groupBy(R.prop('roomId'), messages)
  await Promise.all(
    R.toPairs(grouped).map(async ([roomId, roomMessages]) => {
      await Promise.all(
        R.toPairs(
          R.groupBy(
            message =>
              message.type === 'IMAGE' || message.type === 'VIDEO'
                ? 'MEDIA'
                : message.type,
            roomMessages,
          ),
        ).map(async ([category, typeMessages]) => {
          const { totalCount } =
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            (await sharedMediaStorage.get(roomId, category)) || {}
          if (!totalCount && totalCount !== 0) return
          await sharedMediaStorage.update([roomId, category], {
            totalCount: totalCount + typeMessages.length,
          })
        }),
      )
    }),
  )
}
export async function calcSharedMediaTotalCount(
  roomId: string,
  category: CategoryType,
) {
  const { totalCount } = (await sharedMediaStorage.get(roomId, category)) || {}
  if (totalCount) return

  const messagesCountByType = await getMessageCountByType(roomId)
  await sharedMediaStorage.update([roomId, category], {
    totalCount:
      category === 'MEDIA'
        ? messagesCountByType.IMAGE + messagesCountByType.VIDEO
        : messagesCountByType[category],
  })
}
export async function fillSharedOnOpenMedia(
  roomId: string,
  category: CategoryType,
  iid: number,
) {
  if (!roomId || !iid) return
  const { iid: lastMessageIid } = await getRoomLastMessage(roomId)
  const { iids, lastFetchIid } = (await sharedMediaStorage.get(
    roomId,
    category,
  )) || {
    iids: [] as number[],
  }
  // NOTE: filling shared media items from room last message to biggest iid available in shared media items
  // if no shared media item is available so fill from last message to current opened media iid
  if (!(lastMessageIid && lastMessageIid === lastFetchIid))
    await fillSharedMediaItems(roomId, category, lastMessageIid, iids[0] || iid)

  // NOTE: filling shared media items from the lowest available message to current opened media iid
  // if no shared media item is available so no filling is done
  await fillSharedMediaItems(roomId, category, R.last(iids || []), iid)
  const isInBound = R.last(iids || []) < iid

  // NOTE: filling shared media items from the lowest available message or current opened media iid to 40 messages lower
  // isInBound determines if current iid is bigger than the lowest current available shared media item or is less than that
  // 40 is bucket size for getMessagesByCategory function

  if (!isInBound) {
    await fillSharedMediaItems(
      roomId,
      category,
      R.last(iids) === iid ? iid - 1 : iid,
      null,
    )
  }
}

export async function getNextAndPrevMessagesByType(
  roomId: string,
  iid: number,
  category: CategoryType,
): Promise<NextAndPrevByType> {
  if (!roomId || !iid) return { prev: null, next: null, current: 1, total: 1 }

  await fillSharedOnOpenMedia(roomId, category, iid)
  await calcSharedMediaTotalCount(roomId, category)
  const { iids, totalCount } = (await sharedMediaStorage.get(
    roomId,
    category,
  )) || {
    iids: [] as number[],
  }
  const index = iids.findIndex(id => id === iid)
  if (index === -1) {
    return { prev: null, next: null, current: 1, total: 1 }
  }
  return {
    prev: index + 1 < totalCount ? iids[index + 1] : null,
    next: index - 1 > -1 ? iids[index - 1] : null,
    current: totalCount - index,
    total: totalCount,
  }
}
